import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { I18nProvider } from './contexts/I18nContext'
import AppLayout from './components/layout/AppLayout'
import MarketingLayout from './components/layout/MarketingLayout'
import LoadingScreen from './components/shared/LoadingScreen'
import ErrorBoundary from './components/shared/ErrorBoundary'
import ComingSoon from './pages/ComingSoon'

// Build-time launch gate. Keep eager (not lazy) so the holding page renders
// immediately without a flash of the real shell while a chunk downloads.
const COMING_SOON = import.meta.env.VITE_COMING_SOON === 'true'

// ── Lazy-loaded pages ──────────────────────────────────
const Landing      = lazy(() => import('./pages/Landing'))
const Features     = lazy(() => import('./pages/marketing/Features'))
const Pricing      = lazy(() => import('./pages/marketing/Pricing'))
const About        = lazy(() => import('./pages/marketing/About'))
const Contact      = lazy(() => import('./pages/marketing/Contact'))
const FAQ          = lazy(() => import('./pages/marketing/FAQ'))
const Privacy      = lazy(() => import('./pages/marketing/Privacy'))
const Terms        = lazy(() => import('./pages/marketing/Terms'))
const Login        = lazy(() => import('./pages/auth/Login'))
const Register     = lazy(() => import('./pages/auth/Register'))
const ForgotPass   = lazy(() => import('./pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'))
const AuthCallback= lazy(() => import('./pages/auth/AuthCallback'))
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'))
const VerifyOtp   = lazy(() => import('./pages/auth/VerifyOtp'))
const VerifyTwoFactor = lazy(() => import('./pages/auth/VerifyTwoFactor'))
const Dashboard    = lazy(() => import('./pages/Dashboard'))
const Groups       = lazy(() => import('./pages/groups/Groups'))
const GroupDetail  = lazy(() => import('./pages/groups/GroupDetail'))
const AddExpense   = lazy(() => import('./pages/expenses/AddExpense'))
const SearchExp    = lazy(() => import('./pages/expenses/SearchExpenses'))
const Templates    = lazy(() => import('./pages/expenses/Templates'))
const ExpenseDetail= lazy(() => import('./pages/expenses/ExpenseDetail'))
const EditExpense  = lazy(() => import('./pages/expenses/EditExpense'))
const RecurringExp = lazy(() => import('./pages/expenses/RecurringExpenses'))
const ItemAssign  = lazy(() => import('./pages/expenses/ItemAssignment'))
const JoinGroup   = lazy(() => import('./pages/groups/JoinGroup'))
const Friends      = lazy(() => import('./pages/friends/Friends'))
const SettleUp     = lazy(() => import('./pages/settle/SettleUp'))
const Profile      = lazy(() => import('./pages/Profile'))
const Insights     = lazy(() => import('./pages/Insights'))
const NotFound     = lazy(() => import('./pages/NotFound'))

// Admin pages
const AdminDashboard    = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminUsers        = lazy(() => import('./pages/admin/AdminUsers'))
const AdminUserDetail   = lazy(() => import('./pages/admin/AdminUserDetail'))
const AdminGroups       = lazy(() => import('./pages/admin/AdminGroups'))
const AdminGroupDetail  = lazy(() => import('./pages/admin/AdminGroupDetail'))
const AdminExpenses     = lazy(() => import('./pages/admin/AdminExpenses'))
const AdminExpenseDetail= lazy(() => import('./pages/admin/AdminExpenseDetail'))
const AdminSettlements  = lazy(() => import('./pages/admin/AdminSettlements'))
const AdminAnalytics    = lazy(() => import('./pages/admin/AdminAnalytics'))
const AdminPaymentSettings     = lazy(() => import('./pages/admin/AdminPaymentSettings'))
const AdminIntegrationSettings = lazy(() => import('./pages/admin/AdminIntegrationSettings'))
const AdminAiSettings          = lazy(() => import('./pages/admin/AdminAiSettings'))

// ── Route guards ───────────────────────────────────────
function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) return <LoadingScreen />
  return isAuthenticated ? children : <Navigate to="/login" replace />
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) return <LoadingScreen />
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />
}

function PlatformAdminRoute({ children }) {
  const { isAuthenticated, isPlatformAdmin, loading } = useAuth()
  if (loading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isPlatformAdmin) return <Navigate to="/dashboard" replace />
  return children
}

// ── Routes ─────────────────────────────────────────────
function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Marketing site — wrapped with shared nav + footer */}
        <Route element={<MarketingLayout />}>
          <Route path="/"         element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing"  element={<Pricing />} />
          <Route path="/about"    element={<About />} />
          <Route path="/contact"  element={<Contact />} />
          <Route path="/faq"      element={<FAQ />} />
          <Route path="/privacy"  element={<Privacy />} />
          <Route path="/terms"    element={<Terms />} />
        </Route>

        {/* Auth — standalone screens */}
        <Route path="/login"         element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register"      element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPass /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<PublicRoute><AuthCallback /></PublicRoute>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-otp"  element={<VerifyOtp />} />
        <Route path="/verify-2fa"  element={<VerifyTwoFactor />} />

        {/* Protected — inside AppLayout (user + admin pages share same layout) */}
        <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
          <Route path="/dashboard"           element={<Dashboard />} />
          <Route path="/groups"              element={<Groups />} />
          <Route path="/groups/:id"          element={<GroupDetail />} />
          <Route path="/join/:token"         element={<JoinGroup />} />
          <Route path="/expenses"            element={<SearchExp />} />
          <Route path="/expenses/add"        element={<AddExpense />} />
          <Route path="/expenses/add/:groupId" element={<AddExpense />} />
          <Route path="/expenses/:id"        element={<ExpenseDetail />} />
          <Route path="/expenses/:id/edit"   element={<EditExpense />} />
          <Route path="/expenses/:id/items"  element={<ItemAssign />} />
          <Route path="/recurring"           element={<RecurringExp />} />
          <Route path="/templates"           element={<Templates />} />
          <Route path="/insights"            element={<Insights />} />
          <Route path="/friends"             element={<Friends />} />
          <Route path="/settle"              element={<SettleUp />} />
          <Route path="/settle/:id"          element={<SettleUp />} />
          <Route path="/profile"             element={<Profile />} />

          {/* Platform admin pages — same layout, guarded by isPlatformAdmin */}
          <Route path="/platform"              element={<PlatformAdminRoute><AdminDashboard /></PlatformAdminRoute>} />
          <Route path="/platform/users"        element={<PlatformAdminRoute><AdminUsers /></PlatformAdminRoute>} />
          <Route path="/platform/users/:id"    element={<PlatformAdminRoute><AdminUserDetail /></PlatformAdminRoute>} />
          <Route path="/platform/groups"       element={<PlatformAdminRoute><AdminGroups /></PlatformAdminRoute>} />
          <Route path="/platform/groups/:id"   element={<PlatformAdminRoute><AdminGroupDetail /></PlatformAdminRoute>} />
          <Route path="/platform/expenses"     element={<PlatformAdminRoute><AdminExpenses /></PlatformAdminRoute>} />
          <Route path="/platform/expenses/:id" element={<PlatformAdminRoute><AdminExpenseDetail /></PlatformAdminRoute>} />
          <Route path="/platform/settlements"  element={<PlatformAdminRoute><AdminSettlements /></PlatformAdminRoute>} />
          <Route path="/platform/analytics"    element={<PlatformAdminRoute><AdminAnalytics /></PlatformAdminRoute>} />
          <Route path="/platform/payment-settings"     element={<PlatformAdminRoute><AdminPaymentSettings /></PlatformAdminRoute>} />
          <Route path="/platform/integration-settings" element={<PlatformAdminRoute><AdminIntegrationSettings /></PlatformAdminRoute>} />
          <Route path="/platform/ai-settings"          element={<PlatformAdminRoute><AdminAiSettings /></PlatformAdminRoute>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  if (COMING_SOON) {
    return <ComingSoon />
  }
  return (
    <ErrorBoundary>
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </ErrorBoundary>
  )
}
